import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/Loader";
import BookingJourneySelector from "./BookingJourneySelector";
import CorporateBookingForm from "../new-booking-form/CorporateBookingForm";
import { BOOKING_AS, BOOKING_FORM_STATUS } from "../../config/constant";
import { termsApiErrorMessage } from "../../utilis/common-funtions";
import {
  getApplicantDetails,
  getOppprtunityDetails,
  setKycVerificationActiveStep,
  setIsCorporateFlow,
  setEntity,
  getMasterDataList,
  setResetData,
  setApplicantTotal,
  setActiveStep,
  setIsFromPreview,
  setProjectBrandImage,
  setPartnerHeaderImage,
  setEditPreviewApplicant,
  setProjectId,
  setBrandId,
  getTermsAndCondition,
  setBrandLogo,
} from "../new-booking-form/new-booking-form.slice";

import { useTranslation } from "react-i18next";
import IndividualCustomerForm from "../new-booking-form/IndividualCustomerForm";
import { useHandleRedirection } from "../new-booking-form/hooks/useHandleRedirection";
import SnackbarAlert from "../../shared-components/snackbar-alert/SnackbarAlert";

const BookingHandler = () => {
  const { oppId } = useParams();
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const {
    isFromPreview,
    isLoading,
    applicantData,
  } = useSelector((state) => state.newBookingForm);
  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const [step, setStep] = useState("select");
  const [bookingAs, setBookingAs] = useState("");
  const [selectedJourney, setSelectedJourney] = useState("");
  const [termsJourneyOk, setTermsJourneyOk] = useState(false);
  const [termsErrorMessage, setTermsErrorMessage] = useState("");
  const history = useHistory();
  const { handleRedirection } = useHandleRedirection();

  useEffect(() => {
    if (applicantData?.opportunityId !== oppId) {
      dispatch(setResetData());
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setTermsJourneyOk(false);
      setTermsErrorMessage("");

      try {
        const res = await dispatch(getOppprtunityDetails(`/${oppId}`)).unwrap();
        if (cancelled || !res) {
          return;
        }

        dispatch(setKycVerificationActiveStep(0));

        const projectName = res?.data?.ProjectName;
        if (!projectName) {
          const msg =
            "Project information is missing. Terms and conditions could not be loaded. You cannot continue.";
          setTermsErrorMessage(msg);
          setSnackbarConfig({
            open: true,
            message: msg,
            severity: "error",
          });
          return;
        }

        let termsOk = false;
        try {
          const termsData = await dispatch(
            getTermsAndCondition(`/${projectName}`)
          ).unwrap();
          if (cancelled) {
            return;
          }

          if (termsData) {
            const projectId = termsData?.projectId;
            const brandId = termsData?.brandId;
            if (projectId) dispatch(setProjectId(projectId));
            if (brandId) dispatch(setBrandId(brandId));
            dispatch(setProjectBrandImage(termsData?.projectImage || ""));
            dispatch(setPartnerHeaderImage(termsData?.jvPartnerLogo || ""));
            dispatch(setBrandLogo(termsData?.brandLogo || ""));
            termsOk = true;
            setTermsJourneyOk(true);
          } else {
            const msg =
              "Terms and conditions could not be loaded. You cannot continue.";
            setTermsErrorMessage(msg);
            dispatch(setProjectBrandImage(""));
            dispatch(setPartnerHeaderImage(""));
            dispatch(setBrandLogo(""));
            setSnackbarConfig({
              open: true,
              message: msg,
              severity: "error",
            });
          }
        } catch (err) {
          if (!cancelled) {
            dispatch(setProjectBrandImage(""));
            dispatch(setPartnerHeaderImage(""));
            dispatch(setBrandLogo(""));
            const msg = termsApiErrorMessage(err);
            setTermsErrorMessage(msg);
            setSnackbarConfig({
              open: true,
              message: msg,
              severity: "error",
            });
          }
        }

        if (cancelled || !termsOk) return;

        dispatch(getMasterDataList());

        dispatch(getApplicantDetails(`/${oppId}`))
          .unwrap()
          .then((applicantRes) => {
            if (!applicantRes?.isAgreedOnTerms) {
              dispatch(setEditPreviewApplicant(false));
            }
            if (applicantRes?.bookingAs) {
              setBookingAs(applicantRes?.bookingAs);
              setStep("form");
              dispatch(
                setIsCorporateFlow(
                  applicantRes?.bookingAs === BOOKING_AS.CORPORATE
                )
              );
              dispatch(
                setEntity(
                  applicantRes?.bookingAs === BOOKING_AS.PARTNERSHIP_FIRM
                    ? t("entity.firm")
                    : t("entity.company")
                )
              );
            }
            if (applicantRes) {
              handleRedirection(applicantRes);
            }
            if (applicantRes?.signedPdf === null) {
              if (
                applicantRes?.leegalityData?.isSignLater === true ||
                applicantData?.bookingFormStatus ===
                  BOOKING_FORM_STATUS?.PARTIALLY_SIGNED ||
                applicantData?.bookingFormStatus ===
                  BOOKING_FORM_STATUS?.NOT_SIGNED ||
                applicantData?.bookingFormStatus ===
                  BOOKING_FORM_STATUS?.SIGNED_OFFLINE
              ) {
                // history.push(`/customer-preview/${oppId}`);
              } else {
                dispatch(setApplicantTotal(applicantRes?.noOfApplicants));
                if (!isFromPreview?.value) {
                  dispatch(setActiveStep(applicantRes?.lastStep || 0));
                } else {
                  dispatch(setActiveStep(isFromPreview?.index || 0));
                  dispatch(setIsFromPreview({ value: false, index: null }));
                }
              }
            }
            if (applicantRes?.signedPdf !== null) {
              dispatch(setActiveStep(0));
            }
          })
          .catch(() => {
            setBookingAs("");
            setStep("select");
            dispatch(setResetData());
          });
      } catch {
        if (!cancelled) {
          history.push("/404");
        }
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleJourneySelect = (journey) => {
    // Call the get-booking API again to get updated bookingAs if needed
    setSelectedJourney(journey);
  };

  useEffect(() => {
    if (applicantData?.bookingAs && step === "select") {
      setBookingAs(applicantData?.bookingAs);
      setStep("form");
    }
  }, [applicantData?.bookingAs, step]);

  useEffect(() => {
    if (selectedJourney) {
      setStep(selectedJourney);
      setBookingAs(selectedJourney);
      if (selectedJourney === BOOKING_AS.CORPORATE) {
        dispatch(setIsCorporateFlow(true));
        dispatch(setEntity(t("entity.company")));
      }
      if (selectedJourney === BOOKING_AS.PARTNERSHIP_FIRM) {
        dispatch(setIsCorporateFlow(false));
        dispatch(setEntity(t("entity.firm")));
      }
    }
  }, [selectedJourney]);

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "error",
    });
  };

  if (isLoading && step === "select") {
    return <Loader isLoading={true} />;
  }

  if (step === "select") {
    return (
    <>
      <BookingJourneySelector
        termsJourneyOk={termsJourneyOk}
        onContinue={handleJourneySelect}
        onError={(message) => setSnackbarConfig({
          open: true,
          message,
          severity: "error",
        })
      } />
      <SnackbarAlert
        open={
          snackbarConfig.open ||
          Boolean(!termsJourneyOk && termsErrorMessage)
        }
        message={snackbarConfig.message || termsErrorMessage}
        severity={
          !termsJourneyOk && termsErrorMessage ? "error" : snackbarConfig.severity
        }
        onClose={handleSnackbarClose}
        autoHideDuration={
          !termsJourneyOk && termsErrorMessage ? null : undefined
        }
      />
    </>
    )
  } else {
    if (bookingAs === BOOKING_AS.INDIVIDUAL) {
      return (
      <>
        <IndividualCustomerForm />
        <SnackbarAlert
          open={snackbarConfig.open}
          message={snackbarConfig.message}
          severity={snackbarConfig.severity}
          onClose={handleSnackbarClose}
        />
      </>
      )
    } else {
      return (
      <>
      <CorporateBookingForm />
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
      </>
      )
    }
  }
};

export default BookingHandler;
